/**
 * Sheet 44, Settings › Audit log (mission 10 WP17 T098 to T102; FR-018, FR-016's screen half, FR-022, FR-023
 * items 1, 2 and 5, FR-024, NFR-011, NFR-012; C-002, C-015; A.5, A.3, TX.4, TX.6, D.1, D.6, T.1, TX.3, J.6; spec
 * amendment 20; OR-15; squad U18, U26, U28; research §1.27 "Sheet 44"; contracts/audit-api.md).
 *
 * One identity per tenant (C-002), so there is no user filter and no Auditor view (44/1, 44/3's deviations):
 * - the h1, then the attribution sentence (NFR §4.2 A.5: the log attributes actions to the organization) and
 *   the UTC note, each once per view; Export CSV is the region's one secondary button (§7.1);
 * - `AuditFilters`, whose URL query is the filters' only state: `auditQuery(filters)` and the CSV read the same
 *   filters (SC-010);
 * - `AuditTable` over every loaded page, newest first; "show older" fetches the page `next_cursor` names;
 * - the shared `Class2Statement` with `terms.class2_years` from the window read (44/14; FR-022): a skeleton while
 *   that read is pending and `genericError` after it fails, never a silent gap;
 * - a read that fails while rows are shown (an older page) keeps the rows and adds the `genericError` band.
 * Export CSV calls `exportAuditLog(filters)` (lib/files.ts: credentials, `text/csv` only, saved through a blob),
 * never an `<a href>` to the route, which appends a `download` event and is guarded against cross-site
 * requests (OR-6). A failure renders `auditExportFailed`, never a URL. After every export attempt the list is read
 * again, since the export appends its own event (mission 10 review UI2). Every route here is a read, allowed while
 * read-only, so nothing on the page is gated. Desktop only (C-015): the table's container scrolls, never the page.
 */
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Band } from '@/components/trellis/Band'
import { Class2Statement } from '@/components/trellis/Class2Statement'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ApiError } from '@/lib/api'
import { AUDIT_WRITTEN_TYPES } from '@/lib/api/audit'
import { exportAuditLog } from '@/lib/files'
import { copyText } from '@/lib/provisional-copy'
import { auditQuery, keys, retentionQuery } from '@/lib/queries'
import { SettingsLayout } from '../SettingsLayout'
import { AuditFilters, auditFiltersFrom } from './AuditFilters'
import { AuditTable } from './AuditTable'

function ReadFailure({ error }: { error: unknown }) {
  if (error instanceof ApiError) return <ProblemBand problem={error.problem} />
  return (
    <Band kind="blocked">
      <p>{copyText('genericError')}</p>
    </Band>
  )
}

export default function AuditLog() {
  const [params] = useSearchParams()
  const { filters } = auditFiltersFrom(params)
  const qc = useQueryClient()
  const audit = useInfiniteQuery(auditQuery(filters))
  const retention = useInfiniteQuery(retentionQuery())
  const [exporting, setExporting] = useState(false)
  const [exportFailed, setExportFailed] = useState(false)

  const pages = audit.data?.pages
  const years = retention.data?.pages[0]?.terms.class2_years

  async function onExport() {
    setExporting(true)
    setExportFailed(false)
    try {
      await exportAuditLog(filters)
    } catch {
      setExportFailed(true)
    } finally {
      setExporting(false)
      // Mission 10 review UI2 (FR-018, A.5): the export appends its own event, kept even when the stream is
      // interrupted, so the list is read again.
      void qc.invalidateQueries({ queryKey: keys.audit })
    }
  }

  return (
    <SettingsLayout>
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-h1 text-navy-950">{copyText('crumb.audit')}</h1>
        <Button variant="secondary" loading={exporting} onClick={() => void onExport()}>
          {copyText('auditExportCsv')}
        </Button>
      </div>
      <p className="text-body-sm text-navy-700">{copyText('auditAttribution')}</p>
      <p className="text-caption text-navy-600">{copyText('auditTimezone')}</p>
      {exportFailed ? (
        <Band kind="blocked">
          <p>{copyText('auditExportFailed')}</p>
        </Band>
      ) : null}
      {/* The page's `types`; while a page loads, the same vocabulary as the SPA mirrors it (audit.test.ts). */}
      <AuditFilters types={pages?.[0]?.types ?? AUDIT_WRITTEN_TYPES} />
      {pages === undefined && audit.isError ? (
        <ReadFailure error={audit.error} />
      ) : (
        <>
          {/* Cycle 2 (review finding 1): a read that fails with rows shown (an older page) keeps them and says so,
              as TransmissionLog's PollFailure does. */}
          {audit.isError ? (
            <Band kind="info">
              <p>{copyText('genericError')}</p>
            </Band>
          ) : null}
          <AuditTable events={pages?.flatMap((p) => p.events) ?? []} loading={pages === undefined} emptyText={copyText('auditEmpty')} />
        </>
      )}
      {audit.hasNextPage ? (
        <div>
          <Button variant="secondary" size="sm" loading={audit.isFetchingNextPage} onClick={() => void audit.fetchNextPage()}>
            {copyText('ledgerOlder')}
          </Button>
        </div>
      ) : null}
      {/* Cycle 2 (review finding 1; the sheet 30 ruling): fail closed. The statement needs the server's years, so a
          skeleton holds its place while the window read is pending and genericError says a failed read. */}
      {years !== undefined ? (
        <Class2Statement years={years} />
      ) : retention.isError ? (
        <Band kind="blocked">
          <p>{copyText('genericError')}</p>
        </Band>
      ) : (
        <Skeleton data-class2-pending="" className="h-10 rounded-md" />
      )}
    </SettingsLayout>
  )
}
